import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useActiveWallpaper } from "@/lib/wallpaper";

/**
 * Renders the active custom wallpaper as a fixed <img> layer directly under
 * document.body via a portal. Portaling side-steps every stacking-context
 * trap and Android WebView bugs with `background-attachment: fixed`.
 *
 * Sizing notes:
 *   - `100dvw / 100dvh` (dynamic viewport units) keeps the wallpaper stable
 *     when the Android keyboard opens/closes and when the URL bar collapses
 *     on mobile Chrome. Falls back to plain vw/vh in older browsers.
 *   - `translateZ(0)` forces GPU compositing so the image is promoted to its
 *     own layer — no repaints when siblings scroll or animate.
 */
export default function WallpaperBackdrop() {
  const { wallpaper, fit } = useActiveWallpaper();
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  if (!mounted) return null;
  if (wallpaper.kind !== "custom" || !wallpaper.url) return null;
  const pos = wallpaper.position || { x: 50, y: 50 };
  return createPortal(
    <div
      data-testid="wallpaper-backdrop"
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100dvw",
        height: "100dvh",
        minWidth: "100vw",
        minHeight: "100vh",
        zIndex: 0,
        pointerEvents: "none",
        overflow: "hidden",
        backgroundColor: "#0A0A0A",
        transform: "translateZ(0)",
      }}
    >
      <img
        src={wallpaper.url}
        alt=""
        draggable={false}
        style={{
          width: "100%",
          height: "100%",
          objectFit: fit === "cover" ? "cover" : "contain",
          objectPosition: `${pos.x}% ${pos.y}%`,
          userSelect: "none",
        }}
      />
      {/* Dim overlay so blue message bubbles keep their contrast. */}
      <div style={{ position: "absolute", inset: 0, backgroundColor: "rgba(0,0,0,0.45)" }} />
    </div>,
    document.body,
  );
}
